/**
 * MFReplacementAudit — Phase G
 *
 * Top Replace Candidates view:
 *   - Global top 10 (holding → candidate) pairs sorted by composite delta
 *   - Per-holding sections grouping each user holding with its peer candidates
 *   - For every pair shows side-by-side metric comparison + evidence bullets
 *
 * Workflow: user reviews evidence and decides whether to switch.
 */

import React, { useState, useEffect } from 'react'
import {
  ArrowRight, RefreshCw, TrendingUp, Award, Shield, ChevronDown, ChevronRight,
} from 'lucide-react'
import { mfIntelAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

const fmt    = (v, d = 2) => v != null && !isNaN(v) ? Number(v).toFixed(d) : '—'
const signed = (v, d = 2) => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(d)}`

const RecBadge = ({ rec }) => {
  const styles = {
    HOLD:    'bg-success-100 text-success-700',
    WATCH:   'bg-amber-100   text-amber-700',
    REDUCE:  'bg-orange-100  text-orange-700',
    REPLACE: 'bg-danger-100  text-danger-700',
  }
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${styles[rec] || styles.WATCH}`}>{rec}</span>
}

const ConfBadge = ({ level }) => {
  const s = { HIGH: 'bg-success-100 text-success-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-danger-100 text-danger-700' }
  return <span className={`text-[9px] font-bold px-1 py-0.5 rounded uppercase ${s[level] || s.LOW}`}>{level}</span>
}

const DeltaPill = ({ label, value, suffix = '', positive = true }) => {
  if (value == null) return null
  const goodDirection = positive ? value > 0 : value < 0
  return (
    <div className={`px-2 py-1 rounded-md text-[10px] font-semibold ${goodDirection ? 'bg-success-50 text-success-700' : 'bg-danger-50 text-danger-700'}`}>
      <span className="opacity-70 mr-1">{label}</span>
      {goodDirection ? '+' : ''}{Number(value).toFixed(2)}{suffix}
    </div>
  )
}

const MetricRow = ({ label, currentVal, candidateVal, suffix = '', delta, deltaGood = true, decimals = 2 }) => {
  const isGood = delta != null && (deltaGood ? delta > 0 : delta < 0)
  const isBad  = delta != null && (deltaGood ? delta < 0 : delta > 0)
  return (
    <div className="grid grid-cols-[110px_1fr_1fr_auto] gap-3 items-center text-xs py-1.5">
      <span className="text-gray-500">{label}</span>
      <span className="font-mono text-right text-gray-700">{currentVal != null ? `${fmt(currentVal, decimals)}${suffix}` : '—'}</span>
      <span className={`font-mono text-right font-semibold ${isGood ? 'text-success-700' : isBad ? 'text-danger-700' : 'text-gray-700'}`}>
        {candidateVal != null ? `${fmt(candidateVal, decimals)}${suffix}` : '—'}
      </span>
      <span className={`text-[10px] font-mono w-12 text-right ${isGood ? 'text-success-600' : isBad ? 'text-danger-600' : 'text-gray-400'}`}>
        {delta != null ? `${delta >= 0 ? '+' : ''}${fmt(Math.abs(delta), decimals)}` : ''}
      </span>
    </div>
  )
}

const FundSummary = ({ fund, label }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
    <p className="font-semibold text-sm text-gray-900 leading-tight">{fund.schemeName}</p>
    <p className="text-[11px] text-gray-500 mt-0.5">{fund.amc}</p>
    <div className="flex gap-2 mt-1.5">
      <ConfBadge level={fund.confidence} />
      {fund.rank && <span className="text-[10px] text-gray-500">#{fund.rank}</span>}
      {fund.percentile != null && <span className="text-[10px] text-gray-500">{Math.round(fund.percentile)}p</span>}
    </div>
  </div>
)

const ComparisonCard = ({ pair, idx }) => {
  const [expanded, setExpanded] = useState(false)
  const { holding, candidate, compositeDelta, cagrDelta, sharpeDelta, ddDelta, evidence } = pair

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpanded(e => !e)}>
        {/* Header: rank + holding → candidate */}
        <div className="flex items-start gap-3">
          {idx != null && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold shrink-0">
              {idx + 1}
            </div>
          )}

          <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
            {/* Current holding */}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                Replace <RecBadge rec={holding.recommendation} />
              </p>
              <p className="font-semibold text-sm text-gray-900 truncate">{holding.schemeName}</p>
              <p className="text-[11px] text-gray-500 truncate">{holding.amc} · {holding.subCategory || holding.category}</p>
            </div>

            {/* Arrow */}
            <div className="text-gray-300 text-center">
              <ArrowRight className="w-5 h-5 mx-auto" />
            </div>

            {/* Candidate */}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                With <Award className="w-3 h-3 text-amber-500" />
              </p>
              <p className="font-semibold text-sm text-primary-900 truncate">{candidate.schemeName}</p>
              <p className="text-[11px] text-gray-500 truncate">{candidate.amc}</p>
            </div>
          </div>

          <button className="text-gray-400 hover:text-gray-600 shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Quick delta strip */}
        <div className="flex flex-wrap gap-1.5 mt-3 pl-9">
          <DeltaPill label="Score" value={compositeDelta} />
          <DeltaPill label="3Y CAGR" value={cagrDelta} suffix="%" />
          <DeltaPill label="Sharpe" value={sharpeDelta} />
          <DeltaPill label="Max DD" value={ddDelta} suffix="%" />
        </div>
      </div>

      {/* Expanded: full comparison */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50/50 px-4 py-4 space-y-4">
          {/* Side-by-side identities */}
          <div className="grid grid-cols-2 gap-4">
            <FundSummary fund={holding}  label="Current Holding" />
            <FundSummary fund={candidate} label="Recommended Candidate" />
          </div>

          {/* Metric comparison */}
          <div className="border border-gray-200 bg-white rounded-lg p-3">
            <div className="grid grid-cols-[110px_1fr_1fr_auto] gap-3 pb-1.5 border-b border-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              <span>Metric</span>
              <span className="text-right">Current</span>
              <span className="text-right">Candidate</span>
              <span className="text-right">Delta</span>
            </div>
            <MetricRow label="Composite"  currentVal={holding.compositeScore} candidateVal={candidate.compositeScore} delta={compositeDelta} decimals={1} />
            <MetricRow label="3Y CAGR"    currentVal={holding.cagr3Y}         candidateVal={candidate.cagr3Y}         delta={cagrDelta} suffix="%" />
            <MetricRow label="Sharpe"     currentVal={holding.sharpeRatio}    candidateVal={candidate.sharpeRatio}    delta={sharpeDelta} decimals={3} />
            <MetricRow label="Max DD"     currentVal={holding.maxDrawdown}    candidateVal={candidate.maxDrawdown}    delta={ddDelta} suffix="%" deltaGood={false} />
            <MetricRow label="Percentile" currentVal={holding.percentile}     candidateVal={candidate.percentile}     delta={candidate.percentile != null && holding.percentile != null ? candidate.percentile - holding.percentile : null} suffix="p" decimals={0} />
          </div>

          {/* Evidence bullets */}
          {evidence?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Why this candidate wins</p>
              <div className="space-y-1">
                {evidence.map((e, i) => (
                  <div key={i} className={`text-xs flex items-start gap-2 px-2 py-1.5 rounded ${
                    e.type === 'positive' ? 'bg-success-50/60 text-success-700'
                    : e.type === 'negative' ? 'bg-danger-50/60 text-danger-700'
                    : 'bg-gray-50 text-gray-700'
                  }`}>
                    <span className="font-bold mt-0.5 shrink-0">
                      {e.type === 'positive' ? '✓' : e.type === 'negative' ? '✗' : '•'}
                    </span>
                    <span>{e.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const MFReplacementAudit = () => {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [view,    setView]    = useState('top')   // 'top' | 'byHolding'

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await mfIntelAPI.replacementAudit()
        setData(res.data)
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load replacement audit')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) return <div className="flex items-center justify-center py-16"><LoadingSpinner size="large" /></div>
  if (error)   return <div className="text-center py-16 text-danger-600 text-sm">{error}</div>
  if (!data)   return null

  const topList   = data.topReplaceCandidates || []
  const perHolding = data.perHolding || []
  const totalCandidates = perHolding.reduce((s, h) => s + (h.candidates?.length || 0), 0)
  const holdingsWithCandidates = perHolding.filter(h => h.candidates?.length > 0).length

  return (
    <div className="space-y-4">

      {/* ── Summary strip ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-100 rounded-xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Holdings reviewed</p>
          <p className="text-2xl font-bold text-gray-900">{perHolding.length}</p>
          <p className="text-[10px] text-gray-500">flagged as REPLACE / REDUCE / WATCH</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Have replacements</p>
          <p className="text-2xl font-bold text-amber-900">{holdingsWithCandidates}</p>
          <p className="text-[10px] text-amber-700">candidates that beat current</p>
        </div>
        <div className="bg-gradient-to-br from-success-50 to-emerald-50 border border-success-100 rounded-xl p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-success-700">Total candidates</p>
          <p className="text-2xl font-bold text-success-800">{totalCandidates}</p>
          <p className="text-[10px] text-success-700">qualifying improvements</p>
        </div>
      </div>

      {/* ── View toggle ───────────────────────────────────────────────────── */}
      <div className="flex gap-2">
        {[
          { id: 'top',       label: `Top ${topList.length} Pairs` },
          { id: 'byHolding', label: `By Holding (${holdingsWithCandidates})` },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              view === v.id ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* ── Top global list ───────────────────────────────────────────────── */}
      {view === 'top' && (
        <div className="space-y-2">
          {!topList.length ? (
            <div className="text-center py-12 text-gray-400 text-sm border border-dashed rounded-xl">
              No qualifying replacement candidates found.
              Either your holdings are already strong, or peer scoring hasn't been run yet.
            </div>
          ) : (
            topList.map((pair, idx) => (
              <ComparisonCard key={`${pair.holding.schemeCode}-${pair.candidate.schemeCode}`} pair={pair} idx={idx} />
            ))
          )}
        </div>
      )}

      {/* ── Per-holding view ──────────────────────────────────────────────── */}
      {view === 'byHolding' && (
        <div className="space-y-5">
          {perHolding.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm border border-dashed rounded-xl">
              No holdings flagged for replacement review.
            </div>
          ) : perHolding.map((entry) => (
            <div key={entry.holding.schemeCode} className="space-y-2">
              <div className="flex items-center gap-3 px-2">
                <p className="text-sm font-bold text-gray-800">{entry.holding.schemeName}</p>
                <RecBadge rec={entry.holding.recommendation} />
                <span className="text-xs text-gray-400">{entry.holding.subCategory || entry.holding.category}</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {entry.candidates.length} qualifying {entry.candidates.length === 1 ? 'candidate' : 'candidates'}
                </span>
              </div>
              {entry.candidates.length === 0 ? (
                <div className="ml-2 text-xs text-gray-400 italic py-3 px-4 bg-gray-50 rounded-lg">
                  No peer beats this holding on multiple metrics — keep monitoring.
                </div>
              ) : (
                entry.candidates.map(c => {
                  // Build a pair-shaped object for the card
                  const compositeDelta = (c.compositeScore ?? 0) - (entry.holding.compositeScore ?? 0)
                  const cagrDelta      = c.cagr3Y != null && entry.holding.cagr3Y != null ? c.cagr3Y - entry.holding.cagr3Y : null
                  const sharpeDelta    = c.sharpeRatio != null && entry.holding.sharpeRatio != null ? c.sharpeRatio - entry.holding.sharpeRatio : null
                  const ddDelta        = c.maxDrawdown != null && entry.holding.maxDrawdown != null ? entry.holding.maxDrawdown - c.maxDrawdown : null
                  const pair = {
                    holding: entry.holding,
                    candidate: c,
                    compositeDelta:   +compositeDelta.toFixed(1),
                    cagrDelta:        cagrDelta != null ? +cagrDelta.toFixed(2) : null,
                    sharpeDelta:      sharpeDelta != null ? +sharpeDelta.toFixed(3) : null,
                    ddDelta:          ddDelta != null ? +ddDelta.toFixed(2) : null,
                    evidence:         c.evidence,
                  }
                  return <ComparisonCard key={c.schemeCode} pair={pair} />
                })
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MFReplacementAudit
